import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useCallback, useEffect, useState} from 'react';

import {assert} from 'Asserts';
import {MinimalStorableSummaryWithSchemaId} from 'constants/SampleConstants';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {BioentitySummary} from 'graphql-client/declarative-translations/external/RESTTypes';
import {graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {DESTINATION_WELL_COLUMN_NAME} from 'notebook/integrations/samples/TransferStructuredTableAdapter';
import ColumnSelector from 'notebook/sample-propagation/add-samples-modal/ColumnSelector';
import {SchemaFieldSelectorsDict} from 'notebook/structured-tables/hooks/useSelectorsById';
import {StructuredTable, StructuredTableAdapter} from 'notebook/structured-tables/StructuredTableConstants';
import fetchContainersFromPlateWellPositions, {
  FetchedContainerInfo,
} from 'notebook/structured-tables/util/fetchContainersFromPlateWellPositions';
import {BaseTypedValueType} from 'notebook/tables/TableConstants';
import {findMatchingDestinationColumnIndices} from 'processes/tables/column-mapping/findMatchingDestinationColumnIndex';
import getMatchingColumnConstraints from 'processes/tables/column-mapping/getMatchingColumnConstraints';
import {
  propagateItemsToDestinationTable,
  StorableSummaryWithContainer,
} from 'processes/tables/column-mapping/propagateContentFromSourceToDestinationTable';
import {CreateProcessButtonQuery} from 'processes/tables/create-process-button/__generated/CreateProcessButtonQuery';
import {ProcessTaskStructuredTable} from 'processes/tables/ProcessTaskStructuredTableAdapter';
import {FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import CenteredHorizontalThrobber from 'ui-library/HorizontalThrobber';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import Modal from 'ui-library/modals/Modal';

import {ColumnSummaryQuery, ColumnSummaryQueryVariables} from './__generated/ColumnSummaryQuery';

type ProcessTaskSchema = CreateProcessButtonQuery['processTaskSchemasSupportingPlatformFeature'][number];

export const COLUMN_SUMMARY_QUERY = gql`
  query ColumnSummaryQuery($bioentityIds: [StrictString!]!, $storableIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: WORKFLOWS) {
    bioentities(ids: $bioentityIds) {
      ...BioentitySummaryOrInaccessibleGraphQL
    }
    storables(ids: $storableIds) {
      ... on StorableInterface {
        ...StorableSummaryGraphQL
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('BioentitySummaryOrInaccessible').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('StorableSummary').fragment}
`;

type SummariesById = Record<string, BioentitySummary | MinimalStorableSummaryWithSchemaId>;
type ColumnValues = Array<BioentitySummary> | Array<MinimalStorableSummaryWithSchemaId>;

export interface TaskColumnMappingInfoBase {
  columnName: string;
  columnIcon?: BenchlingIconName;
  hasValues: boolean;
}

export interface TaskColumnMappingInfoWithValues extends TaskColumnMappingInfoBase {
  columnName: string;
  columnValues: ColumnValues;
  columnIcon?: BenchlingIconName;
  hasValues: true;
}

export interface TaskColumnMappingInfoWithIds {
  columnName: string;
  columnType: BaseTypedValueType;
  columnIds: Array<string>;
  columnIcon?: BenchlingIconName;
  hasValues: false;
  shouldFetchPlateInfo: boolean;
}

export type TaskColumnMappingInfo = TaskColumnMappingInfoWithValues | TaskColumnMappingInfoWithIds;

export interface ColumnSummary {
  columnName: string;
  columnIcon?: BenchlingIconName;
}

interface SelectTargetColumnModalSubmitResult {
  taskTable: ProcessTaskStructuredTable | null;
  shouldRunCreateModal: boolean;
}

export enum SelectTaskColumnMappingsModalCreationType {
  PROCESS = 'process',
  REQUEST_V2_SUBMISSION = 'request_v2_submission',
}

export interface SelectTargetColumnModalProps {
  // Source columns that can be mapped
  sourceColumns: Array<TaskColumnMappingInfo>;
  taskSchema: ProcessTaskSchema;
  taskTable: ProcessTaskStructuredTable;
  selectorsById: SchemaFieldSelectorsDict;
  fetchedContainerInfo: FetchedContainerInfo | null;
  summariesById: SummariesById;
  // What the user is creating after submitting this modal
  creationType: SelectTaskColumnMappingsModalCreationType;
  onRequestClose: () => void;
  onRequestSubmit: (result: SelectTargetColumnModalSubmitResult) => void;
}

function getSelectableDestinationColumns(
  sourceItems: ColumnValues,
  taskTable: ProcessTaskStructuredTable,
  taskSchema: ProcessTaskSchema,
  shouldFetchPlateInfo?: boolean,
  fetchedContainerInfo?: FetchedContainerInfo | null
): Array<ColumnSummary> {
  assert(sourceItems.length > 0, 'Source column must have at least 1 value');
  let sourceTablePrimaryColumnSummary = getMatchingColumnConstraints(sourceItems);
  if (shouldFetchPlateInfo) {
    sourceTablePrimaryColumnSummary = {
      ...sourceTablePrimaryColumnSummary,
      schemaIdConstraint: fetchedContainerInfo?.schemaId ?? null,
    };
  }
  const destinationColumnIndices = findMatchingDestinationColumnIndices(
    sourceTablePrimaryColumnSummary,
    taskSchema,
    taskTable
  );
  return destinationColumnIndices.map((destinationColumnIndex) => {
    const column = taskTable.columns[destinationColumnIndex];
    return {
      columnName: column.name,
    };
  });
}

function isColumnDestinationWellForTransfer(column: TaskColumnMappingInfo): boolean {
  return !column.hasValues && column.shouldFetchPlateInfo;
}

function getSelectedSourceItems(
  sourceColumn: TaskColumnMappingInfo,
  summariesById: SummariesById
): {selectedSourceItems: ColumnValues; shouldFetchPlateInfo?: boolean} {
  let selectedSourceItems: ColumnValues = [];
  let shouldFetchPlateInfo: boolean | undefined;
  if (sourceColumn.hasValues) {
    selectedSourceItems = sourceColumn.columnValues;
  } else {
    selectedSourceItems = sourceColumn.columnIds.map((id) => summariesById[id]) as
      | Array<BioentitySummary>
      | Array<MinimalStorableSummaryWithSchemaId>;
    shouldFetchPlateInfo = sourceColumn.shouldFetchPlateInfo;
  }
  return {selectedSourceItems, shouldFetchPlateInfo};
}

export function SelectTaskColumnMappingsModal({
  sourceColumns,
  taskSchema,
  taskTable,
  selectorsById,
  summariesById,
  fetchedContainerInfo,
  creationType,
  onRequestClose,
  onRequestSubmit,
}: SelectTargetColumnModalProps): JSX.Element {
  const [selectedSourceColumnIndex, setSelectedSourceColumnIndex] = useState<number>(() => {
    const firstSelectableSourceColumnIndex = sourceColumns.findIndex((sourceColumn) => {
      // If the source column has values, they are directly mapping selected items (e.g. from search). They won't choose a source column.
      if (sourceColumn.hasValues) {
        return true;
      } else {
        const {selectedSourceItems} = getSelectedSourceItems(sourceColumn, summariesById);
        return getSelectableDestinationColumns(selectedSourceItems, taskTable, taskSchema).length > 0;
      }
    });
    // If no selectable source columns, default to the first column. Let downstream handle this by showing an error message
    return firstSelectableSourceColumnIndex === -1 ? 0 : firstSelectableSourceColumnIndex;
  });
  const [selectedSourceItems, setSelectedSourceItems] = useState<ColumnValues | null>(null);
  // Destination columns are dynamic based on the selected source column,
  // so we need to maintain a list of which ones are selectable
  const [selectableDestinationColumns, setSelectableDestinationColumns] =
    useState<Array<ColumnSummary> | null>(null);
  const [selectedDestinationColumnIndex, setSelectedDestinationColumnIndex] = useState<number>(0);

  const handleSubmit = useCallback((): void => {
    assert(selectedSourceItems != null, 'Selected source items must not be null');
    assert(selectableDestinationColumns != null, 'Selectable destination columns must not be null');
    const selectedSourceItemsWithContainers = selectedSourceItems.map((item, rowNum) =>
      isColumnDestinationWellForTransfer(sourceColumns[selectedSourceColumnIndex])
        ? {
            ...item,
            container: fetchedContainerInfo?.containers?.[rowNum] ?? null,
          }
        : item
    ) as Array<BioentitySummary> | Array<StorableSummaryWithContainer>;
    onRequestSubmit({
      taskTable: propagateItemsToDestinationTable({
        items: selectedSourceItemsWithContainers,
        destinationColumnIndex: taskTable.columns.findIndex(
          ({name}) => name === selectableDestinationColumns[selectedDestinationColumnIndex].columnName
        ),
        destinationTable: taskTable,
      }),
      shouldRunCreateModal: true,
    });
  }, [
    selectedSourceItems,
    selectableDestinationColumns,
    onRequestSubmit,
    taskTable,
    sourceColumns,
    selectedSourceColumnIndex,
    fetchedContainerInfo?.containers,
    selectedDestinationColumnIndex,
  ]);

  useEffect(() => {
    // When a new source column is selected, reset the destination column index
    setSelectedDestinationColumnIndex(0);
    const {selectedSourceItems, shouldFetchPlateInfo} = getSelectedSourceItems(
      sourceColumns[selectedSourceColumnIndex],
      summariesById
    );
    setSelectedSourceItems(selectedSourceItems);
    setSelectableDestinationColumns(
      getSelectableDestinationColumns(
        selectedSourceItems,
        taskTable,
        taskSchema,
        shouldFetchPlateInfo,
        fetchedContainerInfo
      )
    );
  }, [
    selectedSourceColumnIndex,
    sourceColumns,
    taskSchema,
    selectorsById,
    taskTable,
    summariesById,
    fetchedContainerInfo,
  ]);

  // User selected items (e.g. from search) and we want to fill those items into the relevant task field
  const directlyMappingItems =
    sourceColumns[selectedSourceColumnIndex].hasValues && sourceColumns.length === 1;

  // Modal copy changes based on whether the user is mapping from search or a table, and whether they are creating tasks or requests
  const sourceColumnSelectionText =
    creationType === SelectTaskColumnMappingsModalCreationType.PROCESS
      ? "Choose a column from this table to auto-populate as a task field. You'll be able to view the tasks before submitting them."
      : "Choose a column from this table to auto-populate a field in the request. You'll be able to view this information before submitting it.";
  const destinationColumnSelectionText =
    creationType === SelectTaskColumnMappingsModalCreationType.PROCESS
      ? "Choose a task field to auto-populate with the items you selected. You'll be able to view the tasks before submitting them."
      : "Choose a field in the request to auto-populate with the items you selected. You'll be able to view this information before submitting it.";
  const creationTypeText =
    creationType === SelectTaskColumnMappingsModalCreationType.PROCESS ? 'tasks' : 'request';
  const fieldText =
    creationType === SelectTaskColumnMappingsModalCreationType.PROCESS ? 'task field' : 'field';

  return (
    <Modal
      title={`Map a column to create ${creationTypeText}`}
      buttons={[
        {
          text: 'Continue without mapping',
          onClick: () => {
            onRequestSubmit({
              taskTable: null,
              shouldRunCreateModal: true,
            });
          },
        },
        {
          text: 'Map column',
          submit: true,
          disabled: selectableDestinationColumns == null || selectableDestinationColumns.length === 0,
        },
      ]}
      onRequestClose={onRequestClose}
      onRequestSubmit={handleSubmit}
    >
      <div className="u-small-spacing-bottom">
        <span className={css(styles.schemaText)}>{`Creating ${creationTypeText}: ${taskSchema.name}`}</span>
      </div>
      {!directlyMappingItems ? (
        <>
          <div className="u-small-spacing-bottom">
            <span>{sourceColumnSelectionText}</span>
          </div>
          <div className={classNames('u-small-spacing-bottom', css(styles.container))}>
            <ColumnSelector
              label="Select a source column"
              columns={sourceColumns.map((column) => ({
                // For column mapping purposes, mapping from the destination well should still use the destination plate as the primary item,
                // so the column index is the same for both.
                // However, we use different names to make it clear to the user whether they are mapping from the plate or the well.
                name: isColumnDestinationWellForTransfer(column)
                  ? DESTINATION_WELL_COLUMN_NAME
                  : column.columnName,
                icon: column.columnIcon,
              }))}
              onChange={setSelectedSourceColumnIndex}
              selectedColumnIndex={selectedSourceColumnIndex}
              disableWhenOnlyOneColumn={false}
            />
          </div>
          <hr />
        </>
      ) : null}
      {selectableDestinationColumns == null || selectedSourceItems == null ? (
        <CenteredHorizontalThrobber />
      ) : selectableDestinationColumns.length > 0 ? (
        <>
          <div className="u-small-spacing-bottom">
            <span>{destinationColumnSelectionText}</span>
          </div>
          <div className={classNames('u-small-spacing-bottom', css(styles.container))}>
            <ColumnSelector
              label={`Choose the ${fieldText} to auto-populate`}
              columns={selectableDestinationColumns.map((column) => ({
                name: column.columnName,
                icon: column.columnIcon,
              }))}
              onChange={setSelectedDestinationColumnIndex}
              selectedColumnIndex={selectedDestinationColumnIndex}
              disableWhenOnlyOneColumn={false}
            />
          </div>
        </>
      ) : (
        <span className="u-small-spacing-top">
          <InlineBanner
            bannerType={InlineBannerType.WARNING}
            text={`The ${directlyMappingItems ? "selected items don't" : "selected column doesn't"} match any ${fieldText}s in ${taskSchema.name}.`}
            supportingInfo={
              directlyMappingItems
                ? 'You can still continue without mapping.'
                : 'Select a different source column, or continue without mapping.'
            }
          />
        </span>
      )}
    </Modal>
  );
}

export interface QueryingSelectTaskColumnMappingsModalProps {
  sourceColumns: Array<TaskColumnMappingInfo>;
  taskSchema: ProcessTaskSchema;
  taskTable: ProcessTaskStructuredTable;
  selectorsById: SchemaFieldSelectorsDict;
  onRequestClose: () => void;
  onRequestSubmit: (result: SelectTargetColumnModalSubmitResult) => void;
  creationType: SelectTaskColumnMappingsModalCreationType;
  structuredTableAdapter?: StructuredTableAdapter<unknown, unknown>;
  structuredTable?: StructuredTable<unknown, unknown>;
}

export default function QueryingSelectTaskColumnMappingsModal(
  props: QueryingSelectTaskColumnMappingsModalProps
): JSX.Element | null {
  const bioentityIds = props.sourceColumns
    .filter((column) => !column.hasValues && column.columnType === BaseTypedValueType.BIOENTITY)
    .map((column) => (!column.hasValues ? column.columnIds : []))
    .flat();
  const storableIds = props.sourceColumns
    .filter((column) => !column.hasValues && column.columnType === BaseTypedValueType.STORABLES)
    .map((column) => (!column.hasValues ? column.columnIds : []))
    .flat();
  const shouldFetchContainerInfo =
    props.structuredTableAdapter != null &&
    props.structuredTable != null &&
    props.sourceColumns.some((column) => isColumnDestinationWellForTransfer(column));
  const result = useSkippableBenchlingQuery<ColumnSummaryQuery, ColumnSummaryQueryVariables>(
    COLUMN_SUMMARY_QUERY,
    {
      variables: {
        bioentityIds,
        storableIds,
      },
      skip: bioentityIds.length === 0 && storableIds.length === 0,
    }
  );
  const [fetchedContainerInfo, setFetchedContainerInfo] = useState<FetchedContainerInfo | null>(null);
  const [isFetchingContainerInfo, setIsFetchingContainerInfo] = useState(false);

  useEffect(() => {
    const fetchContainerInfo = async (): Promise<void> => {
      if (shouldFetchContainerInfo) {
        setIsFetchingContainerInfo(true);
        const containersFromPlateWellPositions = await fetchContainersFromPlateWellPositions(
          props.structuredTableAdapter!.getSampleInfosForRequest(props.structuredTable!)
        );
        setIsFetchingContainerInfo(false);
        setFetchedContainerInfo(containersFromPlateWellPositions);
      }
    };
    fetchContainerInfo();
  }, [props.structuredTableAdapter, props.structuredTable, shouldFetchContainerInfo]);

  if (result.type === QueryResultType.PENDING || (shouldFetchContainerInfo && isFetchingContainerInfo)) {
    return <CenteredHorizontalThrobber />;
  } else if (result.type === QueryResultType.SUCCESS) {
    const summariesById: SummariesById = {};
    result.data.bioentities.forEach((bioentity) => {
      if (!isInaccessible(bioentity)) {
        summariesById[bioentity.id] = graphQLToREST('BioentitySummary', bioentity);
      }
    });
    result.data.storables.forEach((storable) => {
      if (!isInaccessible(storable)) {
        summariesById[storable.id] = storable;
      }
    });
    let sourceColumns = props.sourceColumns;
    // If fetchedContainerInfo is null, we should filter out the "destination well" column as unselectable
    if (shouldFetchContainerInfo && !isFetchingContainerInfo && fetchedContainerInfo == null) {
      sourceColumns = sourceColumns.filter((column) => !isColumnDestinationWellForTransfer(column));
    }
    return (
      <SelectTaskColumnMappingsModal
        {...props}
        sourceColumns={sourceColumns}
        summariesById={summariesById}
        fetchedContainerInfo={fetchedContainerInfo}
      />
    );
  } else if (result.type === QueryResultType.SKIPPED) {
    return <SelectTaskColumnMappingsModal {...props} summariesById={{}} fetchedContainerInfo={null} />;
  } else {
    return null;
  }
}

const styles = StyleSheet.create({
  container: {
    minWidth: 400,
  },
  schemaText: {
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
});
